import { useTranslation } from 'react-i18next';
import { isDesktop } from 'utils/platform';

// The login screen's loading placeholder (the blinking keypad). It is shown in two places so the
// user keeps seeing the SAME login screen continuously — from opening the app, through the login
// handshake, until the destination page (whatever the backend restores) is actually mounted:
//   1) behind the auth iframe during initial auth (components/ReauthModal), and
//   2) as the pre-auth shell's post-login cover (src/Shell.js).
// Styling comes from the .login / .numbers-panel rules inlined in index.html. It fills its
// container; the parent owns positioning/stacking.
const LoginSkeleton = () => {
  const { t } = useTranslation();

  return (
    <div className="login" style={{ width: '100%', height: '100%' }}>
      <p
        style={{
          height: '80px',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {t('login.enter_or_create_code')}
      </p>
      <div
        style={{
          color: 'var(--tg-theme-hint-color)',
          height: '50px',
          caretColor: 'transparent',
          letterSpacing: '10px',
          pointerEvents: 'none',
          textAlign: 'center',
          position: 'relative',
        }}
      >
        <input
          type="text"
          value="____"
          readOnly
          style={{
            opacity: 0.01,
            color: 'var(--tg-theme-hint-color)',
            height: '50px',
            caretColor: 'transparent',
            letterSpacing: '10px',
            pointerEvents: 'none',
            textAlign: 'center',
          }}
        />
        <span
          style={{
            background: 'var(--tg-theme-bg-color)',
            position: 'absolute',
            top: 0,
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            left: 0,
            fontSize: '22px',
            color: 'var(--tg-theme-hint-color)',
            caretColor: 'transparent',
            letterSpacing: '10px',
            pointerEvents: 'none',
            textAlign: 'center',
          }}
        >
          {isDesktop() && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="30"
              height="30"
              fill="currentColor"
              aria-hidden="true"
              style={{
                color: 'var(--tg-theme-hint-color)',
                opacity: 0.5,
                margin: '0 15px 0 -45px',
              }}
            >
              <path d="M20 5H4c-1.1 0-1.99.9-1.99 2L2 17c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-9 3h2v2h-2V8zm0 3h2v2h-2v-2zM8 8h2v2H8V8zm0 3h2v2H8v-2zm-1 2H5v-2h2v2zm0-3H5V8h2v2zm9 7H8v-2h8v2zm0-4h-2v-2h2v2zm0-3h-2V8h2v2zm3 3h-2v-2h2v2zm0-3h-2V8h2v2z" />
            </svg>
          )}
          ____
        </span>
      </div>
      <div className="numbers-panel-wrapper">
        <div className="numbers-panel">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <div key={num} className="numbers-panel__item">
              <button type="button">{num}</button>
            </div>
          ))}
          <div />
          <div className="numbers-panel__item">
            <button type="button">0</button>
          </div>
          <div className="numbers-panel__item">
            <button type="button">
              <svg
                version="1.1"
                id="Capa_1"
                xmlns="http://www.w3.org/2000/svg"
                x="0px"
                y="0px"
                viewBox="0 0 489.425 489.425"
                transform="translate(-2 0)"
                style={{
                  enableBackground: 'new 0 0 489.425 489.425',
                  height:
                    'calc(min((100vh - 80px - 50px - 5 * 20px) / 4, (100vw - 4 * 20px) / 3) / 1.75)',
                }}
              >
                <title>Backspace</title>
                <path
                  d="M122.825,394.663c17.8,19.4,43.2,30.6,69.5,30.6h216.9c44.2,0,80.2-36,80.2-80.2v-200.7c0-44.2-36-80.2-80.2-80.2h-216.9
                  c-26.4,0-51.7,11.1-69.5,30.6l-111.8,121.7c-14.7,16.1-14.7,40.3,0,56.4L122.825,394.663z M29.125,233.063l111.8-121.8
                  c13.2-14.4,32-22.6,51.5-22.6h216.9c30.7,0,55.7,25,55.7,55.7v200.6c0,30.7-25,55.7-55.7,55.7h-217c-19.5,0-38.3-8.2-51.5-22.6
                  l-111.7-121.8C23.025,249.663,23.025,239.663,29.125,233.063z"
                  fill="var(--tg-theme-hint-color)"
                />
                <path
                  d="M225.425,309.763c2.4,2.4,5.5,3.6,8.7,3.6s6.3-1.2,8.7-3.6l47.8-47.8l47.8,47.8c2.4,2.4,5.5,3.6,8.7,3.6s6.3-1.2,8.7-3.6
                  c4.8-4.8,4.8-12.5,0-17.3l-47.9-47.8l47.8-47.8c4.8-4.8,4.8-12.5,0-17.3s-12.5-4.8-17.3,0l-47.8,47.8l-47.8-47.8
                  c-4.8-4.8-12.5-4.8-17.3,0s-4.8,12.5,0,17.3l47.8,47.8l-47.8,47.8C220.725,297.263,220.725,304.962,225.425,309.763z"
                  fill="var(--tg-theme-hint-color)"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginSkeleton;
